import { faSidebar } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createFileRoute } from '@tanstack/react-router';
import { useRef } from 'react';
import { z } from 'zod';
import { ReviewEmptyState } from '@/app/(client)/questionnaires/-components/ReviewEmptyState';
import { ReviewMainHeader } from '@/app/(client)/questionnaires/-components/ReviewMainHeader';
import { ReviewMainPanel } from '@/app/(client)/questionnaires/-components/ReviewMainPanel';
import { ReviewOutline } from '@/app/(client)/questionnaires/-components/ReviewOutline';
import { ReviewSideList } from '@/app/(client)/questionnaires/-components/ReviewSideList';
import { usePreviewNavigation } from '@/app/(client)/questionnaires/-hooks/usePreviewNavigation';
import { useReviewMainPanel } from '@/app/(client)/questionnaires/-hooks/useReviewMainPanel';
import { useReviewRoute } from '@/app/(client)/questionnaires/-hooks/useReviewRoute';
import { useStickyControls } from '@/app/(client)/questionnaires/-hooks/useStickyControls';
import { isAnswerFilled } from '@/app/(client)/questionnaires/-types';
import { IconButton } from '@/components/shared/IconButton';
import { useTranslator } from '@/hooks/useTranslator';
import { cn } from '@/lib/utils/cn';

const reviewSearchSchema = z.object({
  /** Selected company's request id — set when the user clicks a row
   *  in the side list (or arrives via deep link from the Summary
   *  subtab's companies table). */
  requestId: z.string().optional(),
});

export const Route = createFileRoute(
  '/(client)/questionnaires/(pages)/(detail)/$id/review/',
)({
  validateSearch: search => reviewSearchSchema.parse(search),
  component: ResponsesReviewRoute,
});

function ResponsesReviewRoute() {
  const { id: questionnaireId } = Route.useParams();
  const { requestId } = Route.useSearch();
  const { getTranslation: t } = useTranslator({ page: 'questionnaires' });
  const r = useReviewRoute({ questionnaireId, requestId });
  // Panel state is owned here (not in ReviewMainPanel) so its toolbar
  // controls can live in the full-width header while the sections
  // render in the body below.
  const panel = useReviewMainPanel({ request: r.request });

  // Scroll-spy for the left content menu — observes the questions scroll
  // container and drives the active-section highlight + click-to-scroll.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const nav = usePreviewNavigation({
    sections: r.request?.sections ?? [],
    scrollContainerRef,
  });
  // Condensed control strip (Sync/Start review/Request changes/Complete)
  // once the header scrolls past — same pattern as the builder header.
  const stickyControls = useStickyControls();
  const setScrollContainer = (el: HTMLDivElement | null) => {
    scrollContainerRef.current = el;
    stickyControls.scrollRef(el);
  };

  // Which answers are still missing — drives the red flags in the
  // content menu. A question the responder marked "not available" is
  // answered, not missing (the server's `total_missing_responses`
  // excludes `is_not_available = 1` too), so it never gets a flag.
  const reviewQuestions = r.request?.questions ?? [];
  const unsubmittedQuestionIds = new Set(
    reviewQuestions
      .filter(
        q =>
          !isAnswerFilled(r.answers.getValue(q.questionId)) &&
          !r.answers.notAvailableIds.has(q.questionId),
      )
      .map(q => q.questionId),
  );

  // The completion donut prefers the wire's response counts — they count
  // a question as answered whenever a response row exists, which is what
  // every other progress surface in the product shows (the side rail's
  // bar, the summary KPIs, the legacy review page). Same reasoning as
  // `ResponderSummary`. Take both numbers from `statistics` or neither,
  // so numerator and denominator can never come from different sources.
  const stats = r.request?.statistics;
  const totalQuestions = stats?.totalQuestions ?? reviewQuestions.length;
  const filledQuestions =
    stats?.totalResponses ??
    reviewQuestions.length - unsubmittedQuestionIds.size;

  // Chain "sync to profile" onto a successful approve when the row's
  // sync toggle is on. `useReviewRoute` and `useReviewMainPanel` are
  // independent hooks (the panel needs `r.request`, so it can't be
  // built from inside the route hook) — this composer is where their
  // two pieces of state actually meet.
  //
  // `feedback: 'inline'` keeps this path toast-only. Reviewers approve
  // answers one after another and the staged set defaults to every
  // answered syncable row, so the bulk flow's congratulations modal
  // would otherwise demand a dismissal per approval (DELTA-2428).
  const syncableQuestionIds = new Set(
    panel.syncableQuestions.map(q => q.questionId),
  );
  const onApproveQuestion = r.onApproveQuestion
    ? (questionId: string) => {
        void r.onApproveQuestion?.(questionId).then(succeeded => {
          if (
            succeeded &&
            syncableQuestionIds.has(questionId) &&
            panel.syncQuestionIds.has(questionId)
          ) {
            void panel.confirmSyncProfile(new Set([questionId]), {
              feedback: 'inline',
            });
          }
        });
      }
    : undefined;

  return (
    // Single page scroll: the header scrolls away while the content menu
    // and picker stay pinned (sticky) and the answers flow. The scroll
    // container is the observer root for the outline scroll-spy.
    <div
      ref={setScrollContainer}
      className='flex flex-col flex-1 min-h-0 overflow-y-auto'
    >
      {/* Full-width company detail header — spans the whole page above
          the selector + questions body. Only shown once a company is
          selected. No wrapper div here (the header owns its own padding):
          nesting it inside one gave the sticky control strip a containing
          block barely taller than the header itself, so it had nowhere to
          actually STAY stuck once scrolled past — same reason
          `QuestionnaireDetailHeader` is a direct child of its scroll
          container rather than wrapped. */}
      {r.request !== undefined && (
        <ReviewMainHeader
          request={r.request}
          onStatusChange={r.handleStatusChange}
          isSubmitting={r.isSubmitting}
          onBack={r.clearSelection}
          onOpenSync={panel.openSync}
          syncDisabled={panel.syncableQuestions.length === 0}
          stickyControls={stickyControls}
          {...(r.logoUrl !== undefined ? { logoUrl: r.logoUrl } : {})}
        />
      )}

      <div className='flex items-start gap-8 px-4 pt-4 pb-4 min-[1600px]:px-6 min-[1600px]:pt-6 min-[1600px]:pb-6'>
        {/* Left: content menu — sticky so it stays while answers scroll. */}
        {r.request !== undefined && (
          <aside className='hidden @5xl:flex @5xl:w-72 @5xl:shrink-0 flex-col @5xl:sticky @5xl:top-[calc(var(--questionnaire-header-h,0px)+1rem)] @5xl:max-h-[calc(100dvh-7rem)] overflow-y-auto'>
            <ReviewOutline
              sections={r.request.sections}
              questions={r.request.questions}
              activeSectionId={nav.activeSectionId}
              onSelectSection={nav.scrollToSection}
              onSelectQuestion={nav.scrollToQuestion}
              numberFor={qid => panel.numberByQuestionId.get(qid) ?? 0}
              unsubmittedQuestionIds={unsubmittedQuestionIds}
              filled={filledQuestions}
              total={totalQuestions}
            />
          </aside>
        )}

        {/* Center: the answer cards — flow in the page scroll. */}
        <div
          className={cn(
            'flex-1 min-w-0',
            r.hasSelection ? '' : 'hidden @5xl:block',
          )}
        >
          {r.request !== undefined ? (
            <ReviewMainPanel
              request={r.request}
              panel={panel}
              getValue={r.answers.getValue}
              setValue={r.answers.setValue}
              {...(onApproveQuestion !== undefined
                ? { onApproveQuestion }
                : {})}
              {...(r.onDenyQuestion !== undefined
                ? { onDenyQuestion: r.onDenyQuestion }
                : {})}
            />
          ) : (
            <ReviewEmptyState />
          )}
        </div>

        {/* Right: the company picker — sticky with its own bounded scroll
            (the list virtualizes, so it needs a bounded height). */}
        {r.sideListOpen && (
          <div
            className={cn(
              'flex-col w-full @5xl:w-80 @5xl:shrink-0 @5xl:sticky @5xl:top-[calc(var(--questionnaire-header-h,0px)+1rem)] max-h-[calc(100dvh-7rem)] overflow-hidden',
              r.hasSelection ? 'hidden @5xl:flex' : 'flex',
            )}
          >
            <ReviewSideList
              requests={r.filteredRequests}
              total={r.totalRequests}
              {...(requestId !== undefined
                ? { selectedRequestId: requestId }
                : {})}
              onSelectRequest={r.selectRequest}
              isLoading={r.listIsLoading}
              filters={r.filters}
              onFiltersChange={r.setFilters}
              bulkScopeIds={r.bulkScopeIds}
              onBulkToggle={r.toggleBulkScope}
              allFilteredSelected={r.allFilteredSelected}
              someFilteredSelected={r.someFilteredSelected}
              onToggleSelectAll={r.toggleSelectAll}
              bulkActions={r.bulkActions}
              onInviteCompanies={r.openInvite}
              canInvite={r.canInvite}
              companyLogosById={r.networkCompaniesById}
              onCollapse={r.closeSideList}
              hasNextPage={r.hasNextPage}
              isFetchingNextPage={r.isFetchingNextPage}
              onFetchNextPage={r.fetchNextPage}
            />
          </div>
        )}

        {!r.sideListOpen && (
          <div className='hidden @5xl:flex shrink-0 items-start rounded-lg border border-border bg-card @5xl:sticky @5xl:top-[calc(var(--questionnaire-header-h,0px)+1rem)]'>
            <IconButton
              variant='ghost'
              size='icon-sm'
              tooltip={t('responses.review.sideList.expand')}
              onClick={r.openSideList}
              className='m-2 text-muted-foreground hover:text-foreground'
            >
              <FontAwesomeIcon icon={faSidebar} />
            </IconButton>
          </div>
        )}
      </div>
    </div>
  );
}
